import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One raw data point from GET /api/admin/daily-releases: the source that produced a release and
// the precise UTC instant it was first seen. The server returns UTC on purpose so all timezone
// and hour bucketing happens here, in the admin's browser-local timezone.
export interface DailyReleasePoint {
  source: string;
  firstSeenUtc: string;
}

// A per-source series of 24 hourly counts (index 0 = local midnight..1am, index 23 = 11pm..midnight)
// for the selected local day. Ready to hand straight to Chart.js as a dataset.
export interface DailyReleaseSeries {
  source: string;
  countsByHour: number[];
  total: number;
}

// The direction a day navigation moved, so the panel can pick the matching slide-transition class.
export type SlideDirection = 'prev' | 'next' | null;

// Owns the "Daily Releases" admin report: for a selected local day it loads every release first
// seen that day and buckets them into per-source hourly counts (browser-local time). Defaults to
// today and supports back/forward day navigation; forward is disabled once the selected day is
// today (there is no future data). Mirrors the other singleton admin services — shared state lives
// here and the panel binds to it directly.
@singleton()
export class DailyReleasesAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;

  // The local calendar day currently shown, normalized to local midnight.
  selectedDay: Date = startOfLocalDay(new Date());

  // The direction of the most recent navigation, used to drive the slide transition.
  lastDirection: SlideDirection = null;

  series: DailyReleaseSeries[] = [];

  // The canonical list of every registered scraper source, loaded once per panel session. Used to
  // build a stable, identical legend across all days: each day's series always contains every
  // source (zero-filled when absent) in the same order, so the chart legend and colors never
  // change as you navigate between days.
  allSources: string[] = [];
  private sourcesLoaded: boolean = false;

  // True when the current day's series has at least one release. Used by the panel to overlay a
  // "no releases" message while still reserving the (now always-present) chart space.
  get hasReleases(): boolean {
    return this.series.some((s) => s.total > 0);
  }

  // True when the selected day is today, so the forward arrow should be disabled.
  get isToday(): boolean {
    return this.selectedDay.getTime() === startOfLocalDay(new Date()).getTime();
  }

  // A stable, human-readable label for the selected day in the admin's locale.
  get selectedDayLabel(): string {
    return this.selectedDay.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Loads the report for the selected local day. Computes the [local midnight, next local midnight)
  // window, converts it to UTC ISO strings for the API, then buckets the returned UTC timestamps
  // back into local hours. Safe to call whenever the panel is opened; failures leave a readable
  // error and an empty series list.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const fromLocal = this.selectedDay;
      const toLocal = new Date(fromLocal);
      toLocal.setDate(toLocal.getDate() + 1);

      const params = new URLSearchParams({
        fromUtc: fromLocal.toISOString(),
        toUtc: toLocal.toISOString(),
      });
      const response = await this.http.get(`/api/admin/daily-releases?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const points: DailyReleasePoint[] = await response.json();
      this.series = bucketByLocalHour(points, this.allSources);
    } catch (e: any) {
      this.error = `Could not load the daily releases report: ${e.message}`;
      this.series = [];
    } finally {
      this.loading = false;
    }
  }

  // Loads the canonical list of every registered source once per panel session so the legend can
  // stay identical across days. Best-effort: on failure the report falls back to per-day sources.
  private async loadSources(): Promise<void> {
    if (this.sourcesLoaded) return;
    try {
      const response = await this.http.get('/api/admin/sources');
      if (!response.ok) return;
      const data: Array<{ id?: string }> = await response.json();
      this.allSources = data
        .map((s) => s.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
      this.sourcesLoaded = true;
    } catch {
      // Non-fatal: leave allSources empty and bucket only the sources present each day.
    }
  }

  // Moves to the previous local day and reloads.
  async goPreviousDay(): Promise<void> {
    this.lastDirection = 'prev';
    this.selectedDay = addDays(this.selectedDay, -1);
    await this.load();
  }

  // Moves to the next local day and reloads. No-op once the selected day is today.
  async goNextDay(): Promise<void> {
    if (this.isToday) return;
    this.lastDirection = 'next';
    this.selectedDay = addDays(this.selectedDay, 1);
    await this.load();
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    this.selectedDay = startOfLocalDay(new Date());
    this.lastDirection = null;
    await this.loadSources();
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }
}

// Returns a copy of the date snapped to local midnight (00:00 in the browser's timezone).
function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Returns a new date `days` local days away from `day`, preserving the local-midnight anchor and
// correctly crossing daylight-saving boundaries via setDate.
function addDays(day: Date, days: number): Date {
  const next = new Date(day);
  next.setDate(next.getDate() + days);
  return startOfLocalDay(next);
}

// Groups raw UTC points into one 24-slot hourly series per source, using each point's local hour
// (getHours) so the chart reflects the admin's timezone. To keep the legend identical across days,
// the result always contains every source in `allSources` (zero-filled when absent that day), plus
// any extra sources present in the data but missing from the registered list (defensive, for
// historical/removed sources). Series are ordered alphabetically by source so both membership and
// order — and therefore the legend and per-source colors — stay stable from day to day.
function bucketByLocalHour(
  points: DailyReleasePoint[], allSources: string[]): DailyReleaseSeries[] {
  const bySource = new Map<string, number[]>();

  for (const point of points) {
    const when = new Date(point.firstSeenUtc);
    if (Number.isNaN(when.getTime())) continue;
    const hour = when.getHours();

    let counts = bySource.get(point.source);
    if (!counts) {
      counts = new Array<number>(24).fill(0);
      bySource.set(point.source, counts);
    }
    counts[hour]++;
  }

  // Canonical, stable order: every registered source (sorted) first, then any extra sources found
  // in the data that are not registered, also sorted and de-duplicated.
  const ordered: string[] = [];
  const seen = new Set<string>();
  const add = (name: string): void => {
    if (!seen.has(name)) {
      seen.add(name);
      ordered.push(name);
    }
  };
  [...allSources].sort((a, b) => a.localeCompare(b)).forEach(add);
  Array.from(bySource.keys()).sort((a, b) => a.localeCompare(b)).forEach(add);

  return ordered.map((source) => {
    const countsByHour = bySource.get(source) ?? new Array<number>(24).fill(0);
    return {
      source,
      countsByHour,
      total: countsByHour.reduce((sum, n) => sum + n, 0),
    };
  });
}
